import type { TemplateType } from "@papier/templates-schema";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import { Skeleton } from "@papier/ui/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@papier/ui/components/ui/table";
import { cn } from "@papier/ui/lib/utils";
import { Refresh as Loader2 } from "@papier/ui/components/icons";

export function PendingProjectsComponent() {
  return (
    <div
      role="status"
      aria-label="Loading projects"
      className="grid gap-5 md:grid-cols-2 xl:grid-cols-3"
    >
      {["first", "second", "third"].map((key) => (
        <div key={key} className="space-y-5 rounded-2xl border p-6">
          <Skeleton className="size-12 rounded-xl" />
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  );
}

export function PendingTemplatesComponent() {
  return (
    <div role="status" aria-label="Loading templates" className="space-y-6">
      <Skeleton className="h-16 w-full rounded-xl" />
      <div className="flex gap-4 rounded-xl border bg-background p-4">
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-8 w-24" />
      </div>
      <div className="space-y-5 rounded-xl border p-5">
        {["one", "two", "three", "four"].map((key) => (
          <Skeleton key={key} className="h-12 w-full" />
        ))}
      </div>
    </div>
  );
}

export function PendingProjectComponent() {
  return (
    <div
      role="status"
      className="flex h-[40vh] items-center justify-center gap-2 text-sm text-muted-foreground"
    >
      <Loader2 className="size-4 animate-spin" />
      Loading project…
    </div>
  );
}

export function PendingTemplateComponent() {
  return (
    <div
      role="status"
      className="flex h-[40vh] items-center justify-center gap-2 text-sm text-muted-foreground"
    >
      <Loader2 className="size-4 animate-spin" />
      <p>Loading template…</p>
    </div>
  );
}

export function PendingLogsTableComponent() {
  return (
    <>
      <Skeleton className="mb-3 h-8 w-1/3" />
      <div className="overflow-hidden rounded-lg border border-muted">
        <Table>
          <TableHeader>
            <TableRow>
              {[
                { label: "Recipient" },
                { label: "Type" },
                { label: "Status" },
                { label: "From" },
                { label: "Created" },
                { label: "Sent" },
                { label: "Provider" },
                { label: "", className: "w-[80px]" },
              ].map((col) => {
                return (
                  <TableHead
                    key={col.label}
                    className={cn("px-3 text-secondary-foreground", col.className)}
                  >
                    {col.label}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              // oxlint-disable-next-line no-array-index-key
              <TableRow key={index}>
                <TableCell className="h-10 px-0 pl-3">
                  <Skeleton className="size-full rounded-none" />
                </TableCell>
                <TableCell className="h-10 px-0">
                  <Skeleton className="size-full rounded-none" />
                </TableCell>
                <TableCell className="h-10 px-0">
                  <Skeleton className="size-full rounded-none" />
                </TableCell>
                <TableCell className="h-10 px-0">
                  <Skeleton className="size-full rounded-none" />
                </TableCell>
                <TableCell className="h-10 px-0">
                  <Skeleton className="size-full rounded-none" />
                </TableCell>
                <TableCell className="h-10 px-0">
                  <Skeleton className="size-full rounded-none" />
                </TableCell>
                <TableCell className="h-10 px-0">
                  <Skeleton className="size-full rounded-none" />
                </TableCell>
                <TableCell className="h-10 px-0 pr-3">
                  <Skeleton className="size-full rounded-none" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Skeleton className="mt-3 ml-auto h-8 w-1/3" />
    </>
  );
}

export function PendingLogsChartsComponent() {
  return (
    <div
      role="status"
      aria-label="Loading log charts"
      className="grid gap-4 md:grid-cols-2 xl:grid-cols-4"
    >
      {["status", "channels", "activity"].map((chart) => (
        <Card key={chart} className={cn("min-w-0 gap-4", chart === "activity" && "md:col-span-2")}>
          <CardHeader>
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-36" />
          </CardHeader>
          <CardContent>
            {chart === "activity" ? (
              <Skeleton className="h-72 w-full" />
            ) : (
              <div className="space-y-5">
                <Skeleton className="mx-auto size-44 rounded-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="mx-auto h-3 w-36" />
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function PendingLogPreviewComponent({ type }: { type: TemplateType }) {
  if (type === "email") {
    return (
      <div className="flex h-full flex-col">
        <section className="flex items-center gap-1 border-b px-4 py-2">
          <Skeleton className="h-8 w-30" />
          <div className="ml-auto hidden items-center gap-1 sm:flex">
            <Skeleton className="h-8 w-20" />
          </div>
        </section>

        <section className="flex-1 p-6">
          {/* Subject line */}
          <div className="mb-6">
            <div className="mb-2 flex items-center gap-2">
              <Skeleton className="h-7 w-48" />
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
          </div>

          <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 flex-1 gap-3">
              <Skeleton className="size-10 shrink-0 rounded-full" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 self-end sm:self-start">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="hidden size-9 sm:flex" />
            </div>
          </div>

          <div className="h-[60vh] sm:h-[65vh]">
            <Skeleton className="size-full" />
          </div>

          <div className="flex items-center gap-2 py-5">
            <Skeleton className="h-8 w-40" />
          </div>
        </section>
      </div>
    );
  }
  return <Skeleton className="h-full w-full" />;
}

export function PendingOrganizationComponent() {
  return (
    <div className="flex h-[40vh] items-center justify-center">
      <p className="text-white">Loading org settings...</p>
    </div>
  );
}

export function PendingApiKeysComponent() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="my-0.5 h-5 w-1/3 md:h-5.5" />
        <Skeleton className="mt-1.5 mb-0.5 h-3 w-2/3 md:h-3.5" />
      </CardHeader>
      <CardContent className="h-40 p-6"></CardContent>
    </Card>
  );
}

export function PendingBillingComponent() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="my-0.5 h-5 w-1/3 md:h-5.5" />
        <Skeleton className="mt-1.5 mb-0.5 h-3 w-2/3 md:h-3.5" />
      </CardHeader>
      <CardContent className="h-50 p-6">
        <Skeleton className="size-full" />
      </CardContent>

      <CardFooter className="border-t">
        <Skeleton className="h-8 w-30 md:ms-auto" />
      </CardFooter>
    </Card>
  );
}

export function PendingSuccessComponent() {
  return (
    <div className="flex h-[80svh] items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md border-border shadow-xl">
        <CardHeader className="text-center">
          <Loader2 className="mx-auto mb-4 size-10 animate-spin rounded-full" />
          <CardTitle className="text-xl">Processing Your Subscription</CardTitle>
          <CardDescription>Please wait while we set up your account...</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

export function PendingUsersComponent() {
  return (
    <div className="overflow-hidden rounded-lg border-2 border-muted">
      <Table>
        <TableHeader className="sticky top-0 bg-secondary">
          <TableRow>
            {[
              { label: "User" },
              { label: "Status" },
              { label: "Verification" },
              { label: "Linked Accounts" },
              { label: "Role" },
              { label: "Last Sign In" },
              { label: "Created" },
              { label: "Actions", className: "w-[80px]" },
            ].map((col) => {
              return (
                <TableHead
                  key={col.label}
                  className={cn("px-3 text-secondary-foreground", col.className)}
                >
                  {col.label}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, index) => (
            // oxlint-disable-next-line no-array-index-key
            <TableRow key={index}>
              <TableCell className="p-3">
                <div className="flex items-center gap-4">
                  <Skeleton className="size-8 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[120px]" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </div>
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-6 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <div className="flex">
                  <Skeleton className="size-8 rounded-full" />
                  <Skeleton className="size-8 rounded-full" />
                </div>
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-6 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-6 w-[100px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-4 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-4 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="size-8 rounded-md" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function PendingOrgsComponent() {
  return (
    <div className="overflow-hidden rounded-lg border-2 border-muted">
      <Table>
        <TableHeader className="sticky top-0 bg-secondary">
          <TableRow>
            {[
              { label: "Organization" },
              { label: "Plan" },
              { label: "Members" },
              { label: "Created" },
              { label: "Actions", className: "w-[80px]" },
            ].map((col) => {
              return (
                <TableHead
                  key={col.label}
                  className={cn("px-3 text-secondary-foreground", col.className)}
                >
                  {col.label}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, index) => (
            // oxlint-disable-next-line no-array-index-key
            <TableRow key={index}>
              <TableCell className="p-3">
                <div className="flex items-center gap-4">
                  <Skeleton className="size-8 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[120px]" />
                    <Skeleton className="h-3 w-[120px]" />
                  </div>
                </div>
              </TableCell>
              <TableCell className="p-3">
                <div className="flex gap-1">
                  <Skeleton className="h-6 w-10" />
                  <Skeleton className="h-6 w-10" />
                </div>
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="size-8 w-20 rounded-full" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-4 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="size-8 rounded-md" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
